import { useState, useCallback, useEffect, useRef } from 'react';
import { Pagination, SelectItemProps } from '@ui/src';
import { useLazyGetOrganizationsQuery } from '../store/service';
import { Organization } from '../store/types';

interface ListResponse {
  data: { data: { edges: { node: Organization }[]; pageInfo: Pagination } };
}

const useGetAutocompleteList = () => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [pagination, setPagination] = useState<Pagination | undefined>(
    undefined,
  );
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [selectedList, setSelectedList] = useState<SelectItemProps[]>([]);

  const [organizationSearchText, setOrganizationSearchText] = useState('');

  const [getList, { isFetching, isLoading }] = useLazyGetOrganizationsQuery();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const getAutocompleteListAction = useCallback(
    async (query?: string, cursor?: string, list?: SelectItemProps[]) => {
      let results: SelectItemProps[] = [];

      if (cursor && list) {
        results = ([] as SelectItemProps[]).concat(list);
        setNextPageIsLoading(true);
      }

      const response = (await getList(
        `${query ? `&search=${query}` : ''}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      )) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          if (
            !results.find((resultItem) => resultItem.value === item.node.id)
          ) {
            results.push({
              name:
                item.node.type === 'CORPORATE'
                  ? (item.node.companyName as string)
                  : `${item.node.firstName} ${item.node.lastName}`,
              value: item.node.id,
            });
          }
        });

        setPagination(response?.data?.data?.pageInfo);

        if (query) {
          const companyNameReponse = (await getList(
            query ? `&companyName=${query}` : '',
          )) as ListResponse;

          if (companyNameReponse?.data?.data) {
            companyNameReponse.data.data.edges?.forEach((item) => {
              if (
                !results.find((resultItem) => resultItem.value === item.node.id)
              ) {
                results.push({
                  name: item.node.companyName as string,
                  value: item.node.id,
                });
              }
            });

            if (companyNameReponse?.data?.data?.edges?.length) {
              setPagination(response?.data?.data?.pageInfo);
            }
          }
        }

        setNextPageIsLoading(false);
      }

      setAutocompleteList(results);
    },
    [],
  );

  const onScroll = (e: any) => {
    const { scrollTop } = e.target;

    if (wrapperRef && wrapperRef.current) {
      const { scrollHeight, clientHeight } = wrapperRef.current;

      if (
        pagination?.hasNextPage &&
        scrollTop >= scrollHeight - clientHeight - 20
      ) {
        getAutocompleteListAction(
          organizationSearchText,
          pagination?.endCursor,
          autocompleteList,
        );
      }
    }
  };

  const onFilterScroll = () => {
    getAutocompleteListAction(
      organizationSearchText,
      pagination?.endCursor,
      autocompleteList,
    );
  };

  const onSearch = (query: string) => {
    setOrganizationSearchText(query);

    getAutocompleteListAction(query);
  };

  useEffect(() => {
    getAutocompleteListAction();
  }, [getAutocompleteListAction]);

  return {
    autocompleteList,
    onSearch,
    organizationIsLoading: isLoading || isFetching,
    setOrganizationSearchText,
    organizationSearchText,
    wrapperRef,
    onScroll,
    nextPageIsLoading,
    setSelectedList,
    selectedList,
    onFilterScroll,
    pagination,
  };
};

export default useGetAutocompleteList;
